import { ClipboardList, PackageX, Send } from 'lucide-react';
import type { StockShortageDto } from '@eombor/contracts';
import { Button, Dialog, cn } from '@eombor/ui';
import { t } from '../../../shared/i18n/index.js';

/**
 * QOLDIQ YETMADI — xato emas, TANLOV.
 *
 * Server `INSUFFICIENT_STOCK` qaytarganda odam hech qanday xato
 * qilmagan: u savatni to'ldirgan paytda mahsulot bor edi, boshqa
 * korxona uni oldinroq so'rab ulgurdi. Shuning uchun bu oyna qizil
 * xato matni emas — u uchta ANIQ yo'lni ko'rsatadi:
 *
 *   1. bor qismini hoziroq yuborish;
 *   2. bor qismini yuborib, QOLGANIGA bildirgi tashlash;
 *   3. hech nima qilmaslik — qoralama joyida qoladi.
 *
 * Sonlar SERVERNIKI va bu yerda qayta hisoblanmaydi: ayirmani mijoz
 * o'zi chiqarsa, ikkovi bir xil sonni ikki xil yaxlitlab, oynada «6 ta
 * yetmayapti» deb yozilib 5 ta bilan yuborilardi.
 */

/** Miqdorni uch xonagacha yaxlitlaydi — ustun `numeric(14,3)`. */
const qtyText = (qty: number): string => String(Math.round(qty * 1000) / 1000);

export interface InsufficientStockDialogProps {
  shortages: readonly StockShortageDto[];
  /**
   * Bor qismini yuborish MUMKINMI.
   *
   * Hech bir satrda bo'sh qoldiq qolmagan bo'lsa, «yuborish» bo'sh
   * hujjatni yuborishga urinish bo'lardi — tugma o'chiriladi va yagona
   * ma'noli yo'l bildirgi bo'lib qoladi.
   */
  canTrim: boolean;
  /** Bildirgi yozish ruxsati yo'q bo'lsa, ikkinchi tugma umuman chizilmaydi. */
  canBackorder: boolean;
  busy: boolean;
  error?: string | undefined;
  onTrim: () => void;
  onTrimAndBackorder: () => void;
  onClose: () => void;
}

export const InsufficientStockDialog = ({
  shortages,
  canTrim,
  canBackorder,
  busy,
  error,
  onTrim,
  onTrimAndBackorder,
  onClose,
}: InsufficientStockDialogProps) => (
  <Dialog
    open
    onClose={onClose}
    title={t('requisition.shortTitle')}
    description={t('requisition.shortHint')}
    busy={busy}
    {...(error === undefined ? {} : { error })}
    footer={
      <div className="flex w-full flex-wrap items-center justify-end gap-2">
        <Button variant="ghost" onClick={onClose} disabled={busy}>
          {t('requisition.shortKeepDraft')}
        </Button>
        {canBackorder ? (
          <Button variant="secondary" onClick={onTrimAndBackorder} loading={busy}>
            <ClipboardList size={15} aria-hidden />
            {t('requisition.shortWithBackorder')}
          </Button>
        ) : null}
        <Button onClick={onTrim} disabled={!canTrim} loading={busy}>
          <Send size={15} aria-hidden />
          {t('requisition.shortSendFree')}
        </Button>
      </div>
    }
  >
    <ul className="flex flex-col gap-2">
      {shortages.map((row) => {
        const missing = Math.round((row.requested - row.free) * 1000) / 1000;
        return (
          <li
            key={row.nomenclatureId}
            className={cn(
              'flex flex-col gap-2 rounded-[var(--radius-md)] border border-warn/30',
              'bg-warn-soft px-3 py-2.5',
            )}
          >
            <p className="flex items-start gap-2 text-sm font-medium text-text">
              <PackageX size={15} aria-hidden className="mt-0.5 shrink-0 text-warn" />
              <span className="min-w-0">{row.nomenclatureName}</span>
            </p>

            {/* Uch son yonma-yon: «so'radim / tegadi / yetmadi». Ularni
                gap ichida yozish o'rniga ustun qilib qo'yish sabab:
                odam bir qarashda qaysi songa qarab qaror qilishini
                bilishi kerak. */}
            <dl className="grid grid-cols-3 gap-2 text-center">
              <div>
                <dt className="text-xs text-text-muted">{t('requisition.shortRequested')}</dt>
                <dd className="tabular text-sm font-semibold text-text">
                  {qtyText(row.requested)} {row.unit}
                </dd>
              </div>
              <div>
                <dt className="text-xs text-text-muted">{t('requisition.shortFree')}</dt>
                <dd className="tabular text-sm font-semibold text-success">
                  {qtyText(row.free)} {row.unit}
                </dd>
              </div>
              <div>
                <dt className="text-xs text-text-muted">{t('requisition.shortMissing')}</dt>
                <dd className="tabular text-sm font-semibold text-warn">
                  {qtyText(missing)} {row.unit}
                </dd>
              </div>
            </dl>

            {/* NEGA yetmagani — «omborda yo'q» va «band qilingan» ikki
                xil hodisa va odamning keyingi qadami ham har xil. */}
            {row.pending > 0 ? (
              <p className="text-xs text-text-muted">
                {t('requisition.shortPending', {
                  qty: qtyText(row.pending),
                  unit: row.unit,
                })}
              </p>
            ) : null}
          </li>
        );
      })}
    </ul>
  </Dialog>
);
